import React, {useEffect, useState} from "react";
import {useTranslation} from "next-i18next";
import {useAppDispatch, useAppSelector} from "../../app/hooks";
import {
  selectIsSubmitting,
  selectRemainingQuestions,
  selectSurveyAnswers,
  setActivityLevel
} from "../../redux/survey/macroSurveySlice";
import {ActivityLevel} from "../../models/fitnessProfile";
import {BikingColorIcon, RunColorIcon, SleepingColorIcon, WalkingColorIcon} from "../icons";
import {HeroHeading2} from "../text";
import {MacroSurveyOptionItemView} from "./MacroSurveyOptionItemView";
import {Button} from "../buttons/Button";
import {LocalizationInfo} from "react-i18next";
import {EventLogger} from "../../utils/eventLogger";
import {AnalyticsEvent} from "../../utils/eventProperties";

export const MacroSurveyActivityLevelQuestionView: React.FC<any> = ({  }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const answers = useAppSelector(selectSurveyAnswers)
  const remainingQuestions = useAppSelector(selectRemainingQuestions)
  const isSubmitting = useAppSelector(selectIsSubmitting)

  const getTitle = (activityLevel: ActivityLevel): LocalizationInfo => {
    switch (activityLevel) {
      case ActivityLevel.low:
        return { key: 'macro_survey:macro_survey_title_low' }
      case ActivityLevel.moderate:
        return { key: 'macro_survey:macro_survey_title_moderate' }
      case ActivityLevel.high:
        return { key: 'macro_survey:macro_survey_title_high' }
      case ActivityLevel.veryHigh:
        return { key: 'macro_survey:macro_survey_title_very_high' }
    }
  }

  const getIcon = (activityLevel: ActivityLevel): React.ReactNode => {
    switch (activityLevel) {
      case ActivityLevel.low:
        return <SleepingColorIcon />
      case ActivityLevel.moderate:
        return <WalkingColorIcon />
      case ActivityLevel.high:
        return <RunColorIcon />
      case ActivityLevel.veryHigh:
        return <BikingColorIcon />
    }
  }

  const options: ActivityLevel[] = [ActivityLevel.low, ActivityLevel.moderate, ActivityLevel.high, ActivityLevel.veryHigh]
  const [activityLevelAnswer, setActivityLevelAnswer] = useState<ActivityLevel | undefined>(answers.activityLevel)
  const [isValid, setIsValid] = useState<boolean>(false)

  useEffect(() => {
    EventLogger.logEvent(new AnalyticsEvent({ eventName: 'Screen Visited', screenName: 'Nutrition Survey' }, { 'Survey Question': 'Activity Level' }))
  }, [])

  useEffect(() => {
    if (activityLevelAnswer) {
      setIsValid(true)
    } else {
      setIsValid(false)
    }
  }, [activityLevelAnswer])

  return <div className="flex flex-col space-y-8 lg:space-y-10 items-center w-full">
    <HeroHeading2 className="text-center mx-8">{t('macro_survey:macro_survey_header_title_activity_level')}</HeroHeading2>

    <div className="w-full">
      {options.map(option => {
        return <div key={option.toString()} className="border-b border-fg-gray-surface last:border-b-0">
          <MacroSurveyOptionItemView
            item={{
              title: getTitle(option),
              id: option.toString(),
              isSelected: activityLevelAnswer === option
            }}
            icon={getIcon(option)}
            isLoading={false}
            onItemSelected={() => {
              setActivityLevelAnswer(option)
            }}
          />
        </div>
      })}
    </div>

    <div className="w-full px-5 md:justify-center md:flex">
      <Button
        isDisabled={!isValid}
        isLoading={isSubmitting}
        size="lg"
        isFullWidth={true}
        onClick={() => {
          activityLevelAnswer && dispatch(setActivityLevel(activityLevelAnswer))
        }}
      >
        {remainingQuestions.length ? t('common:common_title_continue') : t('common:common_title_submit')}
      </Button>
    </div>
  </div>
}